import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, ActivityIndicator, RefreshControl } from 'react-native';
import { useBottomSheetScrollableCreator } from '@gorhom/bottom-sheet';
import { FlashList } from '@shopify/flash-list';

import { CommentCard } from './CommentCard';
import { BottomTextInput } from '@/components/ui/BottomTextInput';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { PostsService } from '~/services/domain/PostsService';
import { PhotosService } from '~/services/domain/PhotosService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { CommentsView as CommentsViewModel } from '@/models/Comment';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { CommentAdd01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { useIsFocused } from 'expo-router/react-navigation';
import {
  IContextSheetContentProps,
  ISheetConfig,
  DRAWER_SCROLL_CLASSES,
} from '@/components/ui/ContextSheet';
import { Post } from '@/models/Post';
import { Photo } from '@/models/Photo';
import { plainToClass } from '@/models/transform';
import { useToastMessages } from '@/hooks/useToastMessages';
import { VStack } from '~/components/gluestack/vstack';
import { useInfiniteListUtils, createSkeletonItems } from '@/hooks/useInfiniteListUtils';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { Center } from '~/components/gluestack/center';
import { PostCard } from '@/components/domain/posts/PostCard';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useAccountSettings } from '@/hooks/useAccountSettings';
import { useDismissKeyboardOnScroll } from '@/hooks/useDismissKeyboardOnScroll';
import { ensureFlashListScrollToEnd } from '@/hooks/ensureFlashListScrollToEnd';
import { buildMarkup, type IMediaItem, IMediaShareContext } from '@/models/Media';
import { mediaRegistry } from '@/services/media/registry';

interface CommentsSheetProps extends IContextSheetContentProps {
  payload?: { post?: Post; photo?: Photo; expectsKeyboard?: boolean };
}

export function CommentsSheet({ payload }: CommentsSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const gorhomFlashListScroll = useBottomSheetScrollableCreator();
  const isScreenActive = useIsFocused();
  const isLargeScreen = useIsLargeScreen();
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 7, desktop: 7 },
  });
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { syncPostFromServer } = useQueryCacheUtils();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dismissKeyboardOnScroll = useDismissKeyboardOnScroll();
  const { data: accountSettings } = useAccountSettings();

  // Extract the post or photo from the payload
  const post = payload?.post;
  const photo = payload?.photo;
  const itemId = post?.Id ?? photo?.Id ?? 0;
  const itemType = post ? 'post' : 'photo';

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['comments', itemType, itemId],
    queryFn: async ({ pageParam }) => {
      if (post) {
        return await PostsService.getComments(post, buildPaginationParams(pageParam, pageSize));
      } else if (photo) {
        return await PhotosService.getPhotoComments(
          photo.Id,
          buildPaginationParams(pageParam, pageSize)
        );
      } else {
        throw new Error('Post or photo data is missing');
      }
    },
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<CommentsViewModel, PageParam>) => {
      const items = data.pages.flatMap((page) => page.CommentItems);
      return { ...data, items };
    }, []),
    refetchOnMount: true,
  });

  const isCommentGifPickerEnabled =
    accountSettings?.MediaInComments === true && accountSettings?.MediaSendDisabled !== true;

  const { handleRefresh, isUserRefreshing } = useInfiniteListUtils({
    queryKey: ['comments', itemType, itemId],
    flashListRef,
  });

  const commentsCountHint = post?.CommentsCount ?? photo?.CommentsCount;
  const skeletonCount = Math.min(
    commentsCountHint == null ? 1 : commentsCountHint === 0 ? 2 : commentsCountHint,
    6
  );
  const skeletonItems = useMemo(() => createSkeletonItems(skeletonCount), [skeletonCount]);

  const listEmptyComponent = useMemo(
    () => (
      <View className="w-full flex-1 items-center justify-start overflow-hidden pt-20 md:pt-12">
        <MessageView
          type="warning"
          text={t('comments.empty')}
          icon={CommentAdd01Icon}
          disableHeight
        />
      </View>
    ),
    [t]
  );

  const handleSubmitComment = async (text: string) => {
    if (!itemId) return;
    setIsSubmitting(true);
    try {
      if (post) {
        await PostsService.addComment(post, text);
        await syncPostFromServer(post.Id);
      } else if (photo) {
        await PhotosService.addPhotoComment(photo.Id, text);
        // Update CommentsCount in individual photo cache
        // Use setQueriesData to match all photo queries with this ID (e.g. ['photo', id, { locked: true }])
        queryClient.setQueriesData(
          { queryKey: ['photo', photo.Id] },
          (oldData: Photo | undefined) => {
            if (!oldData) return oldData;
            return plainToClass(Photo, {
              ...oldData,
              CommentsCount: (oldData.CommentsCount ?? 0) + 1,
            });
          }
        );
        // Update CommentsCount in photos list caches
        queryClient.setQueriesData({ queryKey: ['photos'] }, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            PhotosItems:
              page.PhotosItems?.map((ph: Photo) =>
                ph.Id === photo.Id
                  ? plainToClass(Photo, { ...ph, CommentsCount: (ph.CommentsCount ?? 0) + 1 })
                  : ph
              ) ?? page.PhotosItems,
          }));
          const updates: any = { ...oldData, pages: newPages };
          if (oldData.items) {
            updates.items = newPages.flatMap((page: any) => page.PhotosItems ?? []);
          }
          return updates;
        });
      }
      showSuccessToast(t('comments.added'));
      await queryClient.invalidateQueries({ queryKey: ['comments', itemType, itemId] });
      ensureFlashListScrollToEnd(flashListRef);
    } catch (err) {
      showErrorToast(t('comments.add_error'), err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaSelected = async (item: IMediaItem, ctx?: IMediaShareContext) => {
    if (!itemId || !item.Id) return;
    setIsSubmitting(true);
    try {
      const markup = buildMarkup(item.Type, item.Provider, item.Id);
      if (post) {
        await PostsService.addComment(post, markup);
        await syncPostFromServer(post.Id);
      } else if (photo) {
        await PhotosService.addPhotoComment(photo.Id, markup);
        queryClient.setQueriesData(
          { queryKey: ['photo', photo.Id] },
          (oldData: Photo | undefined) => {
            if (!oldData) return oldData;
            return plainToClass(Photo, {
              ...oldData,
              CommentsCount: (oldData.CommentsCount ?? 0) + 1,
            });
          }
        );
        queryClient.setQueriesData({ queryKey: ['photos'] }, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          const newPages = oldData.pages.map((page: any) => ({
            ...page,
            PhotosItems:
              page.PhotosItems?.map((ph: Photo) =>
                ph.Id === photo.Id
                  ? plainToClass(Photo, { ...ph, CommentsCount: (ph.CommentsCount ?? 0) + 1 })
                  : ph
              ) ?? page.PhotosItems,
          }));
          const updates: any = { ...oldData, pages: newPages };
          if (oldData.items) {
            updates.items = newPages.flatMap((page: any) => page.PhotosItems ?? []);
          }
          return updates;
        });
      }

      void mediaRegistry.get(item.Provider)?.triggerShare(item, ctx);
      showSuccessToast(t('comments.added'));
      await queryClient.invalidateQueries({ queryKey: ['comments', itemType, itemId] });
      ensureFlashListScrollToEnd(flashListRef);
    } catch (err) {
      showErrorToast(t('comments.add_error'), err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCommentInput = () => (
    <BottomTextInput
      onSubmit={handleSubmitComment}
      onMediaSelected={handleMediaSelected}
      gifPickerMode={isCommentGifPickerEnabled ? 'enabled' : 'none'}
      isSubmitting={isSubmitting}
      placeholder={t('comments.add_placeholder')}
      autoFocus={payload?.expectsKeyboard}
      keepKeyboardOpenAfterSubmit
    />
  );

  if (error) {
    return <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />;
  }

  return (
    <VStack className="flex-1">
      <View className="min-h-0 flex-1">
        <FlashList
          ref={flashListRef}
          style={{ flex: 1 }}
          data={isPending ? skeletonItems : (data?.items ?? [])}
          {...DRAWER_SCROLL_CLASSES}
          contentContainerClassName="mr-5 flex-grow"
          {...(!isLargeScreen
            ? { renderScrollComponent: gorhomFlashListScroll, bounces: false }
            : {})}
          ListHeaderComponent={
            isLargeScreen && post ? (
              <VStack className="mb-8">
                <PostCard
                  post={post}
                  noTruncate
                  hideRecentComments
                  hideActions
                  disableImageViewer
                  compactImage
                  className="dark:bg-background-0"
                />
              </VStack>
            ) : undefined
          }
          ListEmptyComponent={listEmptyComponent}
          renderItem={({ item }) =>
            isPending ? (
              <CommentCard />
            ) : (
              <CommentCard comment={item} parentPost={post} parentPhoto={photo} />
            )
          }
          keyExtractor={(item, index) => (isPending ? `s-${index}` : `comment-${item.Id}`)}
          // Bucket recycling by cell shape so media comments don't recycle into text comments.
          getItemType={(item) => (isPending ? 'skeleton' : item.Media ? 'media' : 'text')}
          onEndReached={() => {
            if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={onEndReachedThreshold}
          ListFooterComponent={() =>
            !isPending && (isFetchingNextPage || isRefetching) ? (
              <Center className="mb-4">
                <ActivityIndicator color="grey" />
              </Center>
            ) : null
          }
          refreshControl={
            isLargeScreen ? (
              <RefreshControl
                refreshing={isRefetching && !isUserRefreshing}
                onRefresh={handleRefresh}
              />
            ) : undefined
          }
          {...dismissKeyboardOnScroll}
        />
      </View>
      {renderCommentInput()}
    </VStack>
  );
}

CommentsSheet.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'md',
  height: 'lg',
  additionalSnapPoints: ['90%'],
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
  adjustForWebKeyboard: true,
  hideCloseButton: true,
} satisfies ISheetConfig;
